import { omit } from "lodash";

import * as purchaseOrdersApi from "@/apiv2/purchaseOrders";
import {
  STATUS,
  PRICING_STRATEGY,
  PRODUCT_TYPES,
  PRODUCT_TYPE_FLASHING_LABEL,
  PRICING_STRATEGY_LINEAL_METRES_LABEL,
  PRICING_STRATEGY_SQUARE_METRES_LABEL,
  PRICING_STRATEGY_BASIC_QUANTITIES_LABEL,
} from "@/utils/constants";
import { handleError } from "@/redux/utils/error";

import {
  SET_IS_FETCHING_STATUS,
  SET_PRODUCTS,
  SET_NEW_PRODUCT_DATA,
  SET_IS_SAVING_STATUS,
  SET_IS_CONFIRMATION_MODAL_VISIBLE,
  SET_IS_SPLIT_ORDER_MODAL_VISIBLE,
  RESET,
} from "./splitPurchaseOrders.actionTypes";
import * as selectors from "./splitPurchaseOrders.selectors";

export const reset = () => (dispatch) => {
  dispatch({
    type: RESET,
  });
};

export const setIsSplitOrderModalVisible = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_SPLIT_ORDER_MODAL_VISIBLE,
    payload: status,
  });
};

export const setIsFetchingStatus = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_FETCHING_STATUS,
    payload: status,
  });
};

export const setProducts = (data) => (dispatch) => {
  dispatch({
    type: SET_PRODUCTS,
    payload: data,
  });
};

export const setNewProductData = (data) => (dispatch) => {
  dispatch({
    type: SET_NEW_PRODUCT_DATA,
    payload: data,
  });
};

export const setIsSavingStatus = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_SAVING_STATUS,
    payload: status,
  });
};

export const setIsConfirmationModalVisible = (status) => (dispatch) => {
  dispatch({
    type: SET_IS_CONFIRMATION_MODAL_VISIBLE,
    payload: status,
  });
};

export const fetchProducts =
  ({ items, flashingItems }) =>
  async (dispatch) => {
    try {
      dispatch(setIsFetchingStatus(STATUS.PENDING));
      const formattedItems = items.map((i) => ({
        ...i,
        originalQuantity: i.quantity,
        productName: i.displayName,
        pricingStrategy: PRICING_STRATEGY[i.pricingStrategy],
        productType: PRODUCT_TYPES[i.productType],
        isSelected: false,
        lengths: i.lengths.map((l) => ({
          ...l,
          isDeleted: false,
        })),
        squareMetres: i.squareMetres.map((s) => ({
          ...s,
          isDeleted: false,
        })),
      }));

      const formattedFlashingItems = flashingItems.map((f) => ({
        ...f,
        uid: f.drawing.uid,
        isSelected: false,
        productName: `#${f.drawing.drawingNumber + 1} ${f.productName} ${f.colour} ${f.thickness}`,
        productType: PRODUCT_TYPE_FLASHING_LABEL,
        pricingStrategy: PRICING_STRATEGY_LINEAL_METRES_LABEL,
        lengths: f.subitems.map((s) => ({
          ...s,
          quantity: s.amount,
          lengthInMm: s.length,
        })),
      }));
      dispatch(setProducts([...formattedFlashingItems, ...formattedItems]));
      dispatch(setIsFetchingStatus(STATUS.FULFILLED));
    } catch (error) {
      dispatch(setIsFetchingStatus(STATUS.REJECTED));
      handleError(error, dispatch);
    }
  };

export const submitOrder =
  ({ purchaseOrderUid }) =>
  async (dispatch, getState) => {
    const state = getState();

    try {
      dispatch(setIsSavingStatus(STATUS.PENDING));
      const products = selectors.getProducts(state);

      const items = products
        .filter((p) => p.productType !== PRODUCT_TYPE_FLASHING_LABEL && p.isSelected)
        .map((p) => {
          const data = {
            id: p.id,
          };

          if (p.pricingStrategy === PRICING_STRATEGY_BASIC_QUANTITIES_LABEL) {
            data.new_item_quantity = parseFloat(p.quantity);
            data.original_item_quantity = parseFloat(p.originalQuantity) - parseFloat(p.quantity);
          }

          if (p.pricingStrategy === PRICING_STRATEGY_LINEAL_METRES_LABEL) {
            data.new_item_quantity = p.lengths
              .filter((s) => !s.isDeleted)
              .map((s) => omit(s, ["isDeleted"]));

            data.original_item_quantity = p.lengths
              .filter((s) => s.isDeleted)
              .map((s) => omit(s, ["isDeleted"]));
          } else if (p.pricingStrategy === PRICING_STRATEGY_SQUARE_METRES_LABEL) {
            data.new_item_quantity = p.squareMetres
              .filter((s) => !s.isDeleted)
              .map((s) => omit(s, ["isDeleted"]));

            data.original_item_quantity = p.squareMetres
              .filter((s) => s.isDeleted)
              .map((s) => omit(s, ["isDeleted"]));
          }

          return data;
        });

      const flashingItems = products
        .filter((p) => p.productType === PRODUCT_TYPE_FLASHING_LABEL && p.isSelected)
        .map((p) => ({
          id: p.id,
          new_item_quantity: p.lengths
            .filter((s) => !s.isDeleted)
            .map((s) => omit(s, ["isDeleted"])),
          original_item_quantity: p.lengths
            .filter((s) => s.isDeleted)
            .map((s) => omit(s, ["isDeleted"])),
        }));

      await purchaseOrdersApi.postSplitOrder({
        purchaseOrderUid,
        payload: {
          items,
          flashing_items: flashingItems,
        },
      })();

      dispatch(setIsSavingStatus(STATUS.FULFILLED));
    } catch (error) {
      handleError(error, dispatch);
      dispatch(setIsSavingStatus(STATUS.REJECTED));
    }
  };
